import React, { useState, useCallback } from 'react'

/**
 * Hook to detect the aspect ratio of an image and return appropriate CSS class
 *
 * @param threshold - Optional threshold value for determining portrait vs landscape (default: 0.7)
 * @returns Object with aspectRatioClass and onImageLoad callback to pass to Image component
 *
 * Portrait images (taller than wide) get 'imageContentPortrait' for object-fit: contain
 * Landscape/square images get 'imageContentLandscape' for object-fit: cover
 *
 * Usage:
 *   const { aspectRatioClass, onImageLoad } = useImageAspectRatio(0.7)
 *   <Image className={aspectRatioClass} onLoad={onImageLoad} ... />
 */
export const useImageAspectRatio = (threshold: number = 0.7) => {
  // Default to landscape to avoid layout shift (most images are landscape/square)
  const [aspectRatioClass, setAspectRatioClass] = useState<string>(
    'imageContentLandscape'
  )

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget

      // Use naturalWidth/naturalHeight for original image dimensions
      const aspectRatio = img.naturalWidth / img.naturalHeight

      // Only apply 'contain' to tall portrait images (bottles, cans)
      // Threshold is configurable via feature flag to fine-tune the cutoff point
      // Analysis of legacylr.com (998 images) showed median of 0.665 with large cluster
      // at exactly 0.665 (standard bottles). Threshold of 0.7 captures most portrait bottles.
      // Examples:
      //   - Wine/liquor bottles: ~0.3-0.5 → contain (prevents cutoff)
      //   - Beer bottles/cans: ~0.4-0.6 → contain (prevents cutoff)
      //   - Standard bottles: ~0.65-0.7 → contain (prevents cutoff)
      //   - Apparel (t-shirts, etc): ~0.7-0.9 → cover (fills space nicely)
      //   - Square products: ~1.0 → cover
      //   - Landscape food: ~1.2+ → cover
      if (aspectRatio < threshold) {
        setAspectRatioClass('imageContentPortrait')
      } else {
        setAspectRatioClass('imageContentLandscape')
      }
    },
    [threshold]
  )

  return { aspectRatioClass, onImageLoad }
}
